import React, { useEffect, useRef } from "react";
import { Mail, Phone, CheckCircle, Package2, RefreshCw } from "lucide-react";
import "../styles/privacy.css";
import { useTranslation } from "react-i18next";
import { CONTACT_INFO, EMAIL_INFO } from "../constants/info";
import returnPolicy from "../images/return-policy.png";

interface ReturnPolicyPageProps {
  className?: string;
}

const ReturnPolicyPage: React.FC<ReturnPolicyPageProps> = ({
  className = "",
}) => {
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 },
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className={`min-h-screen bg-[#f2e6db] ${className}`}>
      {/* Hero Section */}
      <div className="pt-10 h-48 py-2 sm:h-96 overflow-hidden flex items-center justify-center mx-2">
        {/* Overlay */}
        <img
          src={returnPolicy}
          alt="Return Policy"
          className="w-full h-48 object-cover sm:w-auto sm:h-auto transition-transform bg-[#0d223f] duration-500 group-hover:scale-110"
        />
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 pb-10 pt-5 lg:py-16">
        {/* Introduction */}
        <div
          ref={(el) => {
            sectionsRef.current[0] = el;
          }}
          className="opacity-0 transform translate-y-8 transition-all duration-700 mb-5"
        >
          <p className="text-gray-700 text-lg leading-relaxed">
            {t("priority_title")}
          </p>
        </div>

        {/* Acceptable Cases */}
        <div
          ref={(el) => {
            sectionsRef.current[1] = el;
          }}
          className="opacity-0 transform translate-y-8 transition-all duration-700 mb-10"
        >
          <div className="glass-effect-card rounded-2xl p-8 transition-all duration-300">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {t("Acceptable cases")}
              </h2>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div className="space-y-4 text-right">
              <div className="flex items-start gap-3 group">
                <div className="w-2 h-2 bg-[#2d1b4e] rounded-full mt-2 group-hover:scale-150 transition-transform"></div>
                <p className="text-gray-700 group-hover:text-gray-900 transition-colors">
                  {t("There is a manufacturing defect in the product")}
                </p>
              </div>
              <div className="flex items-start gap-3 group">
                <div className="w-2 h-2 bg-[#2d1b4e] rounded-full mt-2 group-hover:scale-150 transition-transform"></div>
                <p className="text-gray-700 group-hover:text-gray-900 transition-colors">
                  {t("The product does not meet the required specifications")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Conditions */}
        <div
          ref={(el) => {
            sectionsRef.current[2] = el;
          }}
          className="opacity-0 transform translate-y-8 transition-all duration-700 mb-10"
        >
          <div className="glass-effect-card rounded-2xl p-8 transition-all duration-300">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {t("Conditions")}
              </h2>
              <Package2 className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex items-start gap-3 group">
              <div className="w-2 h-2 bg-[#2d1b4e] rounded-full mt-2 group-hover:scale-150 transition-transform"></div>
              <p className="text-gray-700 hover:text-gray-900 transition-colors">
                {t("Return request within 3 days from the date of receipt")}
              </p>
            </div>
          </div>
        </div>

        {/* Return Steps */}
        <div
          ref={(el) => {
            sectionsRef.current[3] = el;
          }}
          className="opacity-0 transform translate-y-8 transition-all duration-700 mb-10"
        >
          <div className="glass-effect-card rounded-2xl p-8 transition-all duration-300">
            <div className="flex items-center gap-3 mb-8">
              <h2 className="text-2xl font-bold">{t("Return steps")}</h2>
              <RefreshCw className="w-8 h-8" />
            </div>

            <div className="space-y-6">
              <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm hover:bg-opacity-20 transition-all duration-300 transform hover:scale-105">
                <p className="text-lg mb-4">
                  {t("1. Contact us via mail or WhatsApp")}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    <span>{EMAIL_INFO}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    <span dir="ltr" className="inline-block">
                      {CONTACT_INFO}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm hover:bg-opacity-20 transition-all duration-300 transform hover:scale-105">
                <p className="text-lg">
                  {t("2. Send the order number and the reason for the return")}
                </p>
              </div>

              <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm hover:bg-opacity-20 transition-all duration-300 transform hover:scale-105">
                <p className="text-lg">
                  {t("3. We will reply and coordinate to pick up the product")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Refund */}
        <div
          ref={(el) => {
            sectionsRef.current[4] = el;
          }}
          className="opacity-0 transform translate-y-8 transition-all duration-700 mb-10"
        >
          <div className="glass-effect-card rounded-2xl p-8 transition-all duration-300">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {t("Refund")}
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3 group">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-2 group-hover:scale-150 transition-transform"></div>
                <p className="text-gray-700 group-hover:text-gray-900 transition-colors">
                  {t("Refund1")}
                </p>
              </div>
              <div className="flex items-start gap-3 group">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-2 group-hover:scale-150 transition-transform"></div>
                <p className="text-gray-700 group-hover:text-gray-900 transition-colors">
                  {t("Refund2")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Us */}
        <div
          ref={(el) => {
            sectionsRef.current[5] = el;
          }}
          className="opacity-0 transform translate-y-8 transition-all duration-700"
        >
          <div className="glass-effect-warm rounded-2xl p-8 text-center transition-all duration-300 transform hover:scale-105">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t("Contact us")}
            </h2>
            <p className="text-gray-700 mb-6">
              {t(
                "We are happy for you to contact us for any inquiries or feedback",
              )}
            </p>
            <div className="space-y-3 max-w-md mx-auto">
              <a
                href="mailto:info@maagoud.qa"
                className="flex items-center justify-center gap-2 text-gray-700 hover:text-amber-700 transition-colors group"
              >
                <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span className="text-lg">{EMAIL_INFO}</span>
              </a>
              <a
                href="tel:+97471075177"
                className="flex items-center justify-center gap-2 text-gray-700 hover:text-amber-700 transition-colors group"
              >
                <Phone className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span dir="ltr" className="inline-block text-lg">
                  {CONTACT_INFO}
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicyPage;
